import * as icons from "./side-menu-icons";
import { CursorType } from "../../cursor-type";
import { TipName } from "shared/datamodel/schemas";
import { MondayLogo } from "frontend/icons/monday-logo";
import { useIsCIMReorderEnabled } from "utils/url-utils";
import styles from "../side-menu.module.css";
import cn from "classnames";

export interface MenuItem {
  type: CursorType;
  iconRenderer: (isSelected: boolean) => JSX.Element;
  tooltip?: string;
  available: boolean;
  tipName?: TipName;
  hidden?: boolean;
  isHighlighted?: (cursor: CursorType) => boolean;
}

const PointerIconRenderer = () => {
  const isCimReorderEnabled = useIsCIMReorderEnabled();
  return isCimReorderEnabled ? icons.pointerIconCIM : icons.pointerIcon;
};

export const pointerMenuItem: MenuItem = {
  type: CursorType.pointer,
  iconRenderer: PointerIconRenderer,
  tooltip: "Select",
  available: true,
};

export const moveMenuItem: MenuItem = {
  type: CursorType.move,
  iconRenderer: () => icons.handIcon,
  tooltip: "Move",
  available: true,
};

export const templatesMenuItem: MenuItem = {
  type: CursorType.template,
  iconRenderer: () => icons.templatesIcon,
  tooltip: "Add template",
  available: true,
  tipName: TipName.walkthroughAddTemplate,
};

export const textMenuItem: MenuItem = {
  type: CursorType.text,
  iconRenderer: () => icons.textIcon,
  tooltip: "Text",
  available: true,
};

export const stickyNoteMenuItem: MenuItem = {
  type: CursorType.stickyNote,
  iconRenderer: () => icons.stickyNoteIcon,
  tooltip: "Sticky Note",
  available: true,
};

export const shapeMenuItem: MenuItem = {
  type: CursorType.shape,
  iconRenderer: () => icons.shapeIcon,
  tooltip: "Shape",
  available: true,
};

export const connectorMenuItem: MenuItem = {
  type: CursorType.connector,
  iconRenderer: () => icons.connectorIcon,
  tooltip: "Connector",
  available: true,
};

export const mindmapMenuItem: MenuItem = {
  type: CursorType.mindmap,
  iconRenderer: () => icons.mindmapIcon,
  tooltip: "Mind Map",
  available: true,
};

export const penMenuItem: MenuItem = {
  type: CursorType.draw,
  iconRenderer: () => icons.penIcon,
  tooltip: "Pen",
  available: true,
};

export const taskCardMenuItem: MenuItem = {
  type: CursorType.taskCard,
  iconRenderer: () => icons.taskIcon,
  tooltip: "Task",
  available: true,
};

export const taskMenuItem: MenuItem = {
  type: CursorType.task,
  iconRenderer: () => icons.taskIcon,
  tooltip: "Task",
  available: true,
};

export const commentMenuItem = (enableComments: boolean, cordEnabled: boolean): MenuItem => ({
  type: CursorType.comment,
  iconRenderer: () => icons.commentIcon,
  tooltip: enableComments ? "Comment" : "Loading comments...",
  available: enableComments,
  hidden: cordEnabled == false,
  tipName: TipName.walkthroughDropComment,
});

export const fileMenuItem: MenuItem = {
  type: CursorType.file,
  iconRenderer: () => icons.uploadFileIcon,
  tooltip: "Upload file",
  available: true,
};

export const frameMenuItem: MenuItem = {
  type: CursorType.frame,
  iconRenderer: () => icons.frameIcon,
  tooltip: "Frame",
  available: true,
  tipName: TipName.walkthroughCreateFrame,
};

export const cardStackMenuItem: MenuItem = {
  type: CursorType.cardStack,
  iconRenderer: () => icons.cardStackIcon,
  tooltip: "Card Stack",
  isHighlighted: (cursor: CursorType) => cursor === CursorType.cardStack || cursor === CursorType.liveIntegration,
  available: true,
};

export const tableMenuItem: MenuItem = {
  type: CursorType.table,
  iconRenderer: () => icons.tableIcon,
  tooltip: "Table",
  available: true,
};

const MondayIntegrationIconRenderer = () => {
  const isCimReorderEnabled = useIsCIMReorderEnabled();
  return isCimReorderEnabled ? (
    <div>
      <div className={styles.mondayIntegrationIcon}>
        <MondayLogo width={22} height={22} />
        Import
      </div>
    </div>
  ) : (
    <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
      <MondayLogo width={22} height={22} />
    </div>
  );
};

export const mondayIntegrationMenuItem: MenuItem = {
  type: CursorType.mondayIntegration,
  iconRenderer: MondayIntegrationIconRenderer,
  tooltip: "monday.com integration",
  available: true,
};

export const mondayIntegrationMenuItemCIM: MenuItem = {
  type: CursorType.mondayIntegration,
  iconRenderer: () => (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        borderRadius: "6px",
        border: "1px solid #EBEDF3",
        color: "#1E1E1E",
        font: "12px/normal Poppins",
        height: "100%",
        gap: "9px",
      }}
    >
      <MondayLogo width={22} height={22} />
      Import
    </div>
  ),
  tooltip: "Import board items",
  available: true,
};

export const ganttMenuItem: MenuItem = {
  type: CursorType.gantt,
  iconRenderer: () => icons.ganttIcon,
  tooltip: "Gantt Chart",
  available: true,
};

const NounIconIconRenderer = () => {
  const isCimReorderEnabled = useIsCIMReorderEnabled();
  return isCimReorderEnabled ? icons.nounIconCIM : icons.nounIcon;
};

export const nounIconsMenuItem: MenuItem = {
  type: CursorType.nounIcon,
  iconRenderer: NounIconIconRenderer,
  tooltip: "Icons",
  available: true,
};

export const threeDotsMenuItem: MenuItem = {
  type: CursorType.sideMenuOverflow,
  iconRenderer: () => icons.threeDotsIcon,
  available: true,
  isHighlighted: (cursor: CursorType) => cursor === CursorType.sideMenuOverflow,
};

export const getMenuItems = (newTaskEnabled: boolean, showMondayFeatures: boolean) => {
  const menuItems = [
    pointerMenuItem,
    templatesMenuItem,
    textMenuItem,
    stickyNoteMenuItem,
    shapeMenuItem,
    penMenuItem,
    newTaskEnabled ? taskMenuItem : taskCardMenuItem,
    fileMenuItem,
    frameMenuItem,
    nounIconsMenuItem,
    ganttMenuItem,
    tableMenuItem,
    mindmapMenuItem,
    cardStackMenuItem,
  ] as MenuItem[];

  if (showMondayFeatures) {
    menuItems.push(mondayIntegrationMenuItem);
  }

  return menuItems;
};

const simpleIcon = (highlight: boolean, icon: JSX.Element) => (
  <div className={cn(styles.cimItemContainer, highlight && styles.cimItemContainerSelected)}>{icon}</div>
);

const shapeIconCIM = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="50"
    height="50"
    fill="none"
    viewBox="0 0 45 50"
    className={styles["svg"]}
  >
    <defs>
      <filter
        id="a"
        width="44.5"
        height="44.6"
        x="0"
        y="3.4"
        colorInterpolationFilters="sRGB"
        filterUnits="userSpaceOnUse"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feColorMatrix in="SourceAlpha" result="hardAlpha" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" />
        <feOffset />
        <feGaussianBlur stdDeviation="2" />
        <feComposite in2="hardAlpha" operator="out" />
        <feColorMatrix values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.15 0" />
        <feBlend in2="BackgroundImageFix" result="effect1_dropShadow_1121_41" />
        <feBlend in="SourceGraphic" in2="effect1_dropShadow_1121_41" result="shape" />
      </filter>
    </defs>
    <g filter="url(#a)">
      <path
        stroke="#00B875"
        strokeLinecap="round"
        strokeWidth="1.3"
        d="M24 16.4c3.1-1.6 9.7-4.1 11.5-1.2 2.2 3.6-8 6-5.2 9.2 1.6 2 9.2-3.3 9.5.5.1 1.6-1.9 1.7-3.4 5.5"
      />
      <circle cx="21.6" cy="35.5" r="7.8" fill="#FFE9EF" stroke="#FFB4C9" strokeWidth="1.3" />
      <g>
        <path
          fill="#E4D2FF"
          fillOpacity=".9"
          d="M11.3 8.1c.5-1 1.9-1 2.4 0l7.1 12.5c.6 1-.1 2-1.2 2H5.4c-1 0-1.7-1-1.2-2l7-12.5Z"
        />
        <path
          stroke="#BB9AEC"
          strokeOpacity=".9"
          strokeWidth="1.3"
          d="M11.9 8.5c.2-.6 1-.6 1.3 0l7 12.4c.3.5 0 1.1-.6 1.1H5.4c-.6 0-1-.6-.6-1.1l7-12.4Z"
        />
      </g>
      <path stroke="#00B875" strokeLinecap="round" strokeWidth="1.3" d="m35 28.4 1.2 2.5c0 .1.1.2.2.1l2.5-1.2" />
    </g>
  </svg>
);

export const cimMenuItems = {
  pointerMenuItem: {
    type: CursorType.pointer,
    iconRenderer: (highlight: boolean) => simpleIcon(highlight, icons.pointerIconCIM),
    tooltip: "Select",
    available: true,
  },
  shapeMenuItem: {
    type: CursorType.shape,
    iconRenderer: (_highlight: boolean) => (
      <div className={styles.cimLargeIcon} style={{ justifyContent: "center", width: "50px" }}>
        {shapeIconCIM}
      </div>
    ),
    tooltip: "Shapes",
    available: true,
  },
  textMenuItem: {
    type: CursorType.text,
    iconRenderer: (highlight: boolean) => simpleIcon(highlight, icons.textIconCim),
    tooltip: "Text",
    available: true,
  },
  stickyNoteMenuItem: {
    type: CursorType.stickyNote,
    iconRenderer: (_highlight: boolean) => (
      <div
        className={styles.tiltOnHover}
        style={{ width: "52px", height: "50px", position: "relative", display: "flex", alignItems: "flex-end" }}
      >
        {/* bottom note: never moves */}
        <svg
          style={{ position: "absolute", left: 0, bottom: "-5px", width: "45px", height: "45px" }}
          xmlns="http://www.w3.org/2000/svg"
          width="50"
          height="50"
          fill="none"
          viewBox="0 0 50 50"
        >
          <defs>
            <filter id="b">
              <feDropShadow dx="0" dy="2" stdDeviation="1.5" floodColor="black" floodOpacity=".25" />
            </filter>
          </defs>
          <rect fill="currentcolor" x="5" y="5" width="40" height="40" filter="url(#b)" />
          <rect width="23.5" height="2.5" x="9.5" y="9.5" fill="#000" opacity={0.1} rx="1.3" />
        </svg>

        {/* top node, moves on hover */}

        <svg
          className={styles.tiltTarget}
          style={{ position: "absolute", left: 0, bottom: "-5px", width: "45px", height: "45px" }}
          xmlns="http://www.w3.org/2000/svg"
          width="50"
          height="50"
          fill="none"
          viewBox="0 0 50 50"
        >
          <defs>
            <filter id="b">
              <feDropShadow dx="0" dy="2" stdDeviation="1.5" floodColor="black" floodOpacity=".25" />
            </filter>
          </defs>
          <rect fill="currentcolor" x="5" y="5" width="40" height="40" filter="url(#b)" />
          <rect width="23.5" height="2.5" x="9.5" y="9.5" fill="#000" opacity={0.1} rx="1.3" />
        </svg>
      </div>
    ),
    tooltip: "Sticky Note",
    available: true,
  },
  penMenuItem: {
    type: CursorType.draw,
    // todo: pass currentColor to the pen icon
    iconRenderer: (_highlight: boolean) => <div className={styles.cimLargeIcon}>{icons.cimPenIcon}</div>,
    tooltip: "Pen",
    available: true,
  },
  nounIconsMenuItem: {
    type: CursorType.nounIcon,
    iconRenderer: (highlight: boolean) => simpleIcon(highlight, icons.nounIconCIM),
    tooltip: "Icons",
    available: true,
  },
  frameMenuItem: {
    type: CursorType.frame,
    iconRenderer: (highlight: boolean) => simpleIcon(highlight, icons.frameIconCim),
    tooltip: "Frame",
    available: true,
  },
  fileMenuItem: {
    type: CursorType.file,
    iconRenderer: (highlight: boolean) => simpleIcon(highlight, icons.imageIconCIM),
    tooltip: "Upload",
    available: true,
  },
  templatesMenuItem: {
    type: CursorType.template,
    iconRenderer: (highlight: boolean) => simpleIcon(highlight, icons.templatesIconCim),
    tooltip: "Templates",
    available: true,
    tipName: TipName.walkthroughAddTemplate,
  },
  mondayIntegrationMenuItemCIM: {
    type: CursorType.mondayIntegration,
    iconRenderer: (highlight: boolean) => (
      <div
        className={cn(styles.cimItemContainer, highlight && styles.cimItemContainerSelected)}
        style={{
          alignItems: "center",
          gap: "8px",
          paddingInline: "16px",
          font: "12px Poppins",
          border: "1px solid #EBEDF3",
          minWidth: "fit-content",
        }}
      >
        <MondayLogo width={13} />
        Import
      </div>
    ), // todo
    tooltip: "Import board items",
    available: true,
  },
  cimWidgetsMenuItem: {
    type: CursorType.cimWidgets,
    iconRenderer: (highlight: boolean) => {
      return (
        <div
          className={cn(styles.cimItemContainer, highlight && styles.cimItemContainerSelected)}
          style={{
            alignItems: "center",
            gap: "8px",
            paddingInline: "16px",
            font: "12px Poppins",
            border: "1px solid #EBEDF3",
            minWidth: "fit-content",
          }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="9" height="8" viewBox="0 0 9 8" fill="none">
            <path d="M1.08008 3.95508H7.80008" stroke={"#0F0F0F"} strokeLinecap="round" />
            <path d="M4.48633 0.640625L4.48633 7.36062" stroke={"#0F0F0F"} strokeLinecap="round" />
          </svg>
          Widgets
        </div>
      );
    },
    available: true,
    tooltip: "Add widget",
    isHighlighted: (cursor: CursorType) => cursor === CursorType.cimWidgets,
  },
} as const;

export const cimItems = [
  cimMenuItems.pointerMenuItem,
  cimMenuItems.shapeMenuItem,
  cimMenuItems.textMenuItem,
  cimMenuItems.stickyNoteMenuItem,
  cimMenuItems.penMenuItem,
  cimMenuItems.nounIconsMenuItem,
  cimMenuItems.frameMenuItem,
  cimMenuItems.fileMenuItem,
  cimMenuItems.templatesMenuItem,
  cimMenuItems.mondayIntegrationMenuItemCIM,
  cimMenuItems.cimWidgetsMenuItem,
];

export function getMenuItemIdSelector(type: CursorType) {
  return `${type}-menu-item`;
}
